import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import AnimatedText from './AnimatedText';

const faqs = [
  {
    q: 'What exactly does HypeLab do?',
    a: 'We plan, produce, and manage social media content for brands that want real growth — not just pretty feeds. UGC, short-form video, full channel management, and creator-led campaigns.',
  },
  {
    q: 'Which platforms do you cover?',
    a: 'Instagram, TikTok, and YouTube Shorts are our core. We also support LinkedIn and X when it fits the audience. If your people live there, we show up there.',
  },
  {
    q: 'Do I need to provide content, or do you create everything?',
    a: 'We create everything end-to-end — scripting, filming, editing, copy, and scheduling. If you already have raw footage or brand assets, even better; we optimise them.',
  },
  {
    q: 'How fast will I see results?',
    a: 'Early lifts in engagement usually show within 30–60 days. Compounding follower growth and lead flow typically kicks in around month three, once we\'ve tested what your audience responds to.',
  },
  {
    q: 'Who is this best for?',
    a: 'Founders, DTC brands, and creators based in the UK (and beyond) who want social that actually converts — not a generic agency output. If you\'re under 100k followers and ready to invest, we\'re a fit.',
  },
  {
    q: 'How do we start?',
    a: 'Fill out the contact form below. I\'ll review it personally, reply within 48 hours, and book a free 20-minute discovery call to see if we\'re a match.',
  },
];

const FAQItem = ({ f, i, open, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: i * 0.04 }}
    className="border-b border-[#1C1917]/10"
  >
    <button
      onClick={onToggle}
      data-testid={`faq-item-${i}`}
      className="w-full py-6 sm:py-8 flex items-start sm:items-center justify-between gap-6 text-left group"
    >
      <span
        className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1C1917] group-hover:text-[#AA94FF] transition-colors"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {f.q}
      </span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.3 }}
        className="shrink-0 w-10 h-10 rounded-full bg-[#1C1917] text-[#F4F4F3] flex items-center justify-center group-hover:bg-[#AA94FF]"
      >
        <Plus className="w-5 h-5" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="pb-8 pr-16 text-base text-[#78716C] leading-relaxed">{f.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 sm:py-32 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3]"
    >
      <div className="max-w-5xl mx-auto">
        <AnimatedText
          as="h2"
          className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-[#1C1917] mb-16"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          y={50}
        >
          Questions, answered.
        </AnimatedText>

        <div>
          {faqs.map((f, i) => (
            <FAQItem
              key={f.q}
              f={f}
              i={i}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
